/* ============================================================
   PurrPaws Media Mosaic
   Video visibility, lightbox, and Shopify editor support
============================================================ */

(() => {
  const SELECTORS = {
    section: '[data-pp-mosaic]',
    item: '[data-pp-mosaic-item]',
    open: '[data-pp-mosaic-open]',
    lightbox: '[data-pp-mosaic-lightbox]',
    stage: '[data-pp-mosaic-stage]',
    details: '[data-pp-mosaic-details]',
    close: '[data-pp-mosaic-close]',
    meta: '[data-pp-mosaic-meta]'
  };

  const instances = new WeakMap();

  class PurrPawsMediaMosaic {
    constructor(section) {
      this.section = section;
      this.lightbox = section.querySelector(SELECTORS.lightbox);
      this.stage = section.querySelector(SELECTORS.stage);
      this.details = section.querySelector(SELECTORS.details);
      this.activeTrigger = null;
      this.activeGridVideo = null;
      this.videoObserver = null;

      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

      this.initVideos();
      this.initLightbox();
    }

    initVideos() {
      const videos = [...this.section.querySelectorAll('.pp-mosaic__video')];

      if (!videos.length) return;

      videos.forEach((video) => {
        video.muted = true;
        video.playsInline = true;

        if (video.hasAttribute('autoplay')) {
          video.dataset.ppAutoplay = 'true';
        }
      });

      if (!('IntersectionObserver' in window)) {
        videos.forEach((video) => {
          if (video.dataset.ppAutoplay === 'true') {
            this.safePlay(video);
          }
        });
        return;
      }

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            const shouldAutoplay = video.dataset.ppAutoplay === 'true';

            if (!shouldAutoplay) return;

            if (entry.isIntersecting && entry.intersectionRatio >= 0.45 && !document.hidden) {
              this.safePlay(video);
            } else {
              video.pause();
            }
          });
        },
        {
          threshold: [0, 0.45, 0.75]
        }
      );

      videos.forEach((video) => this.videoObserver.observe(video));
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    initLightbox() {
      if (!this.lightbox || !this.stage || !this.details) return;

      this.section.querySelectorAll(SELECTORS.open).forEach((trigger) => {
        trigger.addEventListener('click', () => this.openLightbox(trigger));
      });

      this.lightbox.querySelectorAll(SELECTORS.close).forEach((button) => {
        button.addEventListener('click', () => this.closeLightbox());
      });
    }

    openLightbox(trigger) {
      const item = trigger.closest(SELECTORS.item);
      const sourceMedia = item?.querySelector('.pp-mosaic__media');
      const metaTemplate = item?.querySelector(SELECTORS.meta);

      if (!item || !sourceMedia || !this.lightbox) return;

      this.activeTrigger = trigger;
      this.stage.replaceChildren();
      this.details.replaceChildren();

      let enlargedMedia;

      if (sourceMedia.tagName === 'VIDEO') {
        this.activeGridVideo = sourceMedia;
        sourceMedia.pause();

        enlargedMedia = sourceMedia.cloneNode(true);
        enlargedMedia.removeAttribute('autoplay');
        enlargedMedia.controls = true;
        enlargedMedia.muted = false;
        enlargedMedia.playsInline = true;
        enlargedMedia.preload = 'auto';

        const sourceTime = Number.isFinite(sourceMedia.currentTime)
          ? sourceMedia.currentTime
          : 0;

        enlargedMedia.addEventListener(
          'loadedmetadata',
          () => {
            if (sourceTime > 0 && sourceTime < enlargedMedia.duration) {
              try {
                enlargedMedia.currentTime = sourceTime;
              } catch (error) {
                // Some browsers do not permit seeking until enough data is loaded.
              }
            }

            this.safePlay(enlargedMedia);
          },
          { once: true }
        );
      } else {
        enlargedMedia = sourceMedia.cloneNode(true);
        enlargedMedia.removeAttribute('loading');
      }

      enlargedMedia.className = 'pp-mosaic-lightbox__media';
      this.stage.appendChild(enlargedMedia);

      if (metaTemplate?.content) {
        this.details.appendChild(metaTemplate.content.cloneNode(true));
      }

      const hasDetails = this.details.textContent.trim().length > 0;
      this.lightbox.classList.toggle('pp-mosaic-lightbox--media-only', !hasDetails);

      this.lightbox.hidden = false;
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('pp-mosaic-lightbox-open');
      document.addEventListener('keydown', this.handleDocumentKeydown);

      requestAnimationFrame(() => {
        const closeButton = this.lightbox.querySelector('.pp-mosaic-lightbox__close');
        closeButton?.focus();
      });
    }

    closeLightbox() {
      if (!this.lightbox || this.lightbox.hidden) return;

      const lightboxVideo = this.stage?.querySelector('video');
      lightboxVideo?.pause();

      this.lightbox.hidden = true;
      this.lightbox.setAttribute('aria-hidden', 'true');
      this.lightbox.classList.remove('pp-mosaic-lightbox--media-only');
      document.body.classList.remove('pp-mosaic-lightbox-open');
      document.removeEventListener('keydown', this.handleDocumentKeydown);

      this.stage?.replaceChildren();
      this.details?.replaceChildren();

      if (
        this.activeGridVideo &&
        this.activeGridVideo.dataset.ppAutoplay === 'true' &&
        !document.hidden
      ) {
        this.safePlay(this.activeGridVideo);
      }

      const triggerToRestore = this.activeTrigger;
      this.activeGridVideo = null;
      this.activeTrigger = null;
      triggerToRestore?.focus();
    }

    handleDocumentKeydown(event) {
      if (event.key === 'Escape') {
        this.closeLightbox();
        return;
      }

      if (event.key !== 'Tab' || !this.lightbox || this.lightbox.hidden) return;

      const focusable = [
        ...this.lightbox.querySelectorAll(
          'button:not([disabled]), a[href], video[controls], [tabindex]:not([tabindex="-1"])'
        )
      ].filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleVisibilityChange() {
      const videos = [...this.section.querySelectorAll('.pp-mosaic__video')];

      if (document.hidden) {
        videos.forEach((video) => video.pause());
      }
    }

    safePlay(video) {
      if (!video) return;

      const playPromise = video.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // Autoplay can be blocked by browser or user preferences.
        });
      }
    }

    destroy() {
      this.closeLightbox();
      this.videoObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      document.removeEventListener('keydown', this.handleDocumentKeydown);
      instances.delete(this.section);
    }
  }

  const initializeSection = (section) => {
    if (!section || instances.has(section)) return;

    const instance = new PurrPawsMediaMosaic(section);
    instances.set(section, instance);
  };

  const initializeAll = (root = document) => {
    if (root.matches?.(SELECTORS.section)) {
      initializeSection(root);
    }

    root.querySelectorAll?.(SELECTORS.section).forEach(initializeSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.(SELECTORS.section)
      ? event.target
      : event.target.querySelector?.(SELECTORS.section);

    instances.get(section)?.destroy();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest?.(SELECTORS.item);
    item?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
})();